import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Scale } from "lucide-react";
import { cn } from "@/lib/utils";
import { CartDrawer } from "@/components/shop/CartDrawer";
import { WishlistDrawer } from "@/components/shop/WishlistDrawer";
import { LoyaltyBadge } from "@/components/loyalty/LoyaltyBadge";
import { UserMenu } from "@/components/layout/UserMenu";
import { fetchCollections, ShopifyCollection } from "@/lib/shopify";
import calmistyleLogo from "@/assets/calmistyle-logo.png";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [collections, setCollections] = useState<ShopifyCollection[]>([]);
  const location = useLocation();

  useEffect(() => {
    const loadCollections = async () => {
      try {
        const data = await fetchCollections(10);
        setCollections(data);
      } catch (error) {
        console.error("Error loading collections:", error);
      }
    };
    loadCollections();
  }, []);

  return (
    <header className="fixed top-[44px] md:top-[40px] left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/50 transition-[top] duration-300">
      <nav className="container-calim">
        <div className="flex items-center justify-between h-14 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-2 md:gap-3">
            <img 
              src={calmistyleLogo} 
              alt="CALMISTYLE" 
              width={56}
              height={56}
              className="h-12 md:h-16 w-auto"
            />
            <span className="hidden lg:block font-display text-lg md:text-2xl font-light tracking-wide">
              CALMISTYLE
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">

            {/* Collections Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm uppercase tracking-[0.15em] font-medium text-muted-foreground hover:text-foreground bg-transparent hover:bg-transparent data-[state=open]:bg-transparent p-0 h-auto">
                    Collections
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[280px] gap-1 p-3 bg-background border border-border rounded-md shadow-lg">
                      {collections.map((collection) => (
                        <li key={collection.id}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={`/collections/${collection.handle}`}
                              className="block select-none rounded-sm px-3 py-2 text-sm leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <span className="font-medium">{collection.title}</span>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {collection.products.edges.length} product{collection.products.edges.length > 1 ? 's' : ''}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                      <li className="border-t border-border mt-1 pt-1">
                        <NavigationMenuLink asChild>
                          <Link
                            to="/collections"
                            className="block select-none rounded-sm px-3 py-2 text-sm font-medium leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                          >
                            View all collections
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link
              to="/about"
              className={cn(
                "text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300",
                location.pathname === "/about"
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Our Story
            </Link>

            <Link
              to="/comparatif"
              className={cn(
                "text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 flex items-center gap-1.5",
                location.pathname === "/comparatif"
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Scale className="h-3.5 w-3.5" />
              Compare
            </Link>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="hidden lg:block"><LoyaltyBadge /></div>
            <WishlistDrawer />
            <CartDrawer />
            <UserMenu />
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden items-center space-x-1">
            <LoyaltyBadge />
            <WishlistDrawer />
            <CartDrawer />
            <button
              type="button"
              className="p-2 text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border/50 py-6 animate-fade-in">
            <div className="flex flex-col space-y-4">

              {/* Mobile Collections Accordion */}
              <div>
                <button
                  onClick={() => setCollectionsOpen(!collectionsOpen)}
                  className="flex items-center justify-between w-full text-sm uppercase tracking-[0.15em] font-medium py-2 text-muted-foreground"
                >
                  Collections
                  <ChevronDown className={cn("h-4 w-4 transition-transform", collectionsOpen && "rotate-180")} />
                </button>
                {collectionsOpen && (
                  <div className="pl-4 mt-2 space-y-2 border-l border-border/50">
                    {collections.map((collection) => (
                      <Link
                        key={collection.id}
                        to={`/collections/${collection.handle}`}
                        className="block text-sm py-1.5 text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {collection.title}
                      </Link>
                    ))}
                    <Link
                      to="/collections"
                      className="block text-sm py-1.5 font-medium text-foreground"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      All collections →
                    </Link>
                  </div>
                )}
              </div>

              <Link
                to="/about"
                className={cn(
                  "text-sm uppercase tracking-[0.15em] font-medium py-2 transition-colors",
                  location.pathname === "/about"
                    ? "text-foreground"
                    : "text-muted-foreground"
                )}
                onClick={() => setMobileMenuOpen(false)}
              >
                Our Story
              </Link>

              <Link
                to="/comparatif"
                className={cn(
                  "text-sm uppercase tracking-[0.15em] font-medium py-2 transition-colors flex items-center gap-2",
                  location.pathname === "/comparatif"
                    ? "text-foreground"
                    : "text-muted-foreground"
                )}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Scale className="h-4 w-4" />
                Compare
              </Link>

              <div className="pt-4 border-t border-border/50 flex items-center justify-end">
                <UserMenu />
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};